import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Container,
  Grid,
  Paper,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  LocalShipping as ShippingIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.orderData || {};
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  


  const handleGoHome = () => {
    navigate('/');
  };

  const handleBackToOrders = () => {
    navigate('/orders');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      pb: 8 // Space for bottom navigation
    }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        py: 3,
        px: 2
      }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleGoHome}
              sx={{ 
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Back
            </Button>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                NISTYLEZ Store
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>


        {/* Order Details */}
        <Grid container spacing={3}>
          {/* Main Order Info */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="primary" />
                  Order Details
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <StoreIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Store"
                      secondary="Nistylez Store"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ReceiptIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Order Number"
                      secondary={orderData?.orderNumber || 'N/A'}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <CalendarIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Order Date"
                      secondary={formatDateTime(orderData?.orderDate || orderData?.orderDetails?.orderDate)}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <CreditCardIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Payment Method"
                      secondary={orderData?.paymentMethod || orderData?.orderDetails?.paymentMethod || 'Credit Card (Stripe)'}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ReceiptIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Payment Confirmation"
                      secondary={orderData?.paymentConfirmationNumber || 'N/A'}
                    />
                  </ListItem>
                  
                  
                  
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Delivery Address"
                      secondary={orderData?.deliveryAddress?.street || orderData?.orderDetails?.deliveryAddress?.street || 'N/A'}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Order Items
                </Typography>
                
                <List>
                  {(orderData?.items || orderData?.orderDetails?.items || []).map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <Avatar 
                        src={item.productImage} 
                        sx={{ width: 56, height: 56, mr: 2 }}
                      >
                        {item.productName?.charAt(0)}
                      </Avatar>
                      <ListItemText
                        primary={item.productName}
                        secondary={`Quantity: ${item.quantity}`}
                      />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ${item.totalPrice?.toFixed(2) || '0.00'}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Summary */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 20 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Payment Summary
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Subtotal:</Typography>
                    <Typography>${orderData?.subtotal?.toFixed(2) || orderData?.orderDetails?.subtotal?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Shipping:</Typography>
                    <Typography>${orderData?.shipping?.toFixed(2) || orderData?.orderDetails?.shipping?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                  
                  
                  {(orderData?.metadata?.couponDiscount > 0 || orderData?.couponDiscount > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="success.main">
                        🎉 Coupon Discount {orderData?.appliedCoupon || orderData?.couponCode ? `(${orderData.appliedCoupon || orderData.couponCode})` : ''}:
                      </Typography>
                      <Typography color="success.main">
                        -${(orderData?.metadata?.couponDiscount || orderData?.couponDiscount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  
                  {(orderData?.metadata?.appliedWalletBalance > 0 || orderData?.walletBalanceUsed > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="success.main">💳 Wallet Applied:</Typography>
                      <Typography color="success.main">
                        -${(orderData?.metadata?.appliedWalletBalance || orderData?.walletBalanceUsed || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  {(orderData?.metadata?.appliedGems > 0 || orderData?.gemsUsed > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="success.main">
                        💎 {(orderData?.metadata?.appliedGems || orderData?.gemsUsed || 0)} Gems Applied:
                      </Typography>
                      <Typography color="success.main">
                        -${(((orderData?.metadata?.appliedGems || orderData?.gemsUsed || 0) / 1000) * 10).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Total:
                    </Typography>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        ${orderData?.totalAmount?.toFixed(2) || orderData?.orderDetails?.totalAmount?.toFixed(2) || '0.00'}
                      </Typography>
                  </Box>
                </Box>

                <Chip 
                  label={orderData?.paymentStatus === 'paid' || orderData?.orderDetails?.paymentStatus === 'paid' ? 'Payment Successful' : 'Processing'}
                  color={orderData?.paymentStatus === 'paid' || orderData?.orderDetails?.paymentStatus === 'paid' ? 'success' : 'warning'}
                  sx={{ width: '100%', mb: 2 }}
                />
                
                <Chip 
                  label={orderData?.status === 'confirmed' || orderData?.orderDetails?.status === 'confirmed' ? 'Order Confirmed' : 'Processing'}
                  color={orderData?.status === 'confirmed' || orderData?.orderDetails?.status === 'confirmed' ? 'success' : 'warning'}
                  sx={{ width: '100%' }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
              }
            }}
          >
            Continue Shopping
          </Button>
          
          <Button
            variant="outlined"
            size="large"
            startIcon={<ReceiptIcon />}
            onClick={handleBackToOrders}
            sx={{ flex: 1 }}
          >
            View Orders
          </Button>
        </Box>
      </Container>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};

export default OrderSuccess;
